"use client";

import { Slider } from "@ethera/ui/slider";
import { useEffect, useRef, useState } from "react";
import { CountUp } from "@/components/marketing/count-up";
import { usePersonaStore } from "@/components/marketing/persona/persona-store";
import { Section } from "@/components/marketing/section";
import { SpotlightCard } from "@/components/marketing/spotlight-card";
import { TrialCTA } from "@/components/marketing/trial-cta";
import { trackMarketingEvent } from "@/lib/marketing/analytics";
import { formatINR } from "@/lib/marketing/format";

const SCALE_PRICE = 2499;
const MINUTES_PER_FOLLOW_UP = 5;

const RANGES = {
  students: { min: 20, max: 1000, step: 10, fallback: 120 },
  fee: { min: 300, max: 10000, step: 100, fallback: 1200 },
  late: { min: 0, max: 80, step: 5, fallback: 35 },
} as const;

type RangeKey = keyof typeof RANGES;

function clampToRange(key: RangeKey, raw: number): number {
  const { min, max, step, fallback } = RANGES[key];
  if (Number.isNaN(raw)) return fallback;
  const snapped = Math.round(raw / step) * step;
  return Math.min(max, Math.max(min, snapped));
}

/**
 * Fee-recovery calculator. Purely illustrative arithmetic on the visitor's
 * own numbers — no invented benchmarks. State mirrors into URL params so
 * owners can share their result.
 */
export function CalculatorSection() {
  const persona = usePersonaStore((state) => state.persona);

  const [students, setStudents] = useState<number>(RANGES.students.fallback);
  const [fee, setFee] = useState<number>(RANGES.fee.fallback);
  const [latePct, setLatePct] = useState<number>(RANGES.late.fallback);
  const touchedRef = useRef(false);
  const engagedRef = useRef(false);

  // Read shared state from URL params once on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromParam = (key: RangeKey, param: string | null, set: (value: number) => void) => {
      if (param === null) return;
      set(clampToRange(key, Number.parseInt(param, 10)));
    };
    fromParam("students", params.get("students"), setStudents);
    fromParam("fee", params.get("fee"), setFee);
    fromParam("late", params.get("late"), setLatePct);
  }, []);

  // Mirror state back into the URL (debounced) and report the viewed result.
  useEffect(() => {
    if (!touchedRef.current) return;
    const timer = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      params.set("students", String(students));
      params.set("fee", String(fee));
      params.set("late", String(latePct));
      const query = params.toString();
      window.history.replaceState(null, "", `${window.location.pathname}?${query}#calculator`);
      trackMarketingEvent("calculator_result_viewed", {
        persona,
        students,
        fee,
        latePct,
        lockedUp: Math.round(students * fee * (latePct / 100)),
      });
    }, 900);
    return () => clearTimeout(timer);
  }, [students, fee, latePct, persona]);

  const handleChange = (key: RangeKey, value: number) => {
    touchedRef.current = true;
    if (!engagedRef.current) {
      engagedRef.current = true;
      trackMarketingEvent("calculator_engaged", { persona });
    }
    if (key === "students") setStudents(value);
    if (key === "fee") setFee(value);
    if (key === "late") setLatePct(value);
  };

  const lockedUp = Math.round(students * fee * (latePct / 100));
  const lateCount = Math.round(students * (latePct / 100));
  const hoursChasing = Math.max(1, Math.round((lateCount * MINUTES_PER_FOLLOW_UP) / 60));
  const perStudent = students > 0 ? SCALE_PRICE / students : SCALE_PRICE;
  const paysForItself = lockedUp > 0 && lockedUp * 0.05 >= SCALE_PRICE;

  const sliders = [
    {
      key: "students" as const,
      label: "Students enrolled",
      value: students,
      display: String(students),
    },
    {
      key: "fee" as const,
      label: "Average monthly fee",
      value: fee,
      display: formatINR(fee),
    },
    {
      key: "late" as const,
      label: "Fees that arrive late",
      value: latePct,
      display: `${latePct}%`,
    },
  ];

  return (
    <Section
      id="calculator"
      tone="tinted"
      eyebrow="Do the sums"
      title="What does chasing fees cost you?"
      subtitle="Your numbers, your answer. Drag the sliders — the result updates as you go and lives in the page address, so you can share it."
    >
      <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2">
        {/* Inputs */}
        <div className="space-y-8">
          {sliders.map((slider) => (
            <div key={slider.key}>
              <div className="mb-3 flex items-baseline justify-between">
                <span className="text-sm font-medium text-foreground">{slider.label}</span>
                <span className="text-sm font-bold tabular-nums text-brand-deep">
                  {slider.display}
                </span>
              </div>
              <Slider
                aria-label={slider.label}
                min={RANGES[slider.key].min}
                max={RANGES[slider.key].max}
                step={RANGES[slider.key].step}
                value={[slider.value]}
                onValueChange={([value]) => handleChange(slider.key, value)}
              />
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            Illustrative only — these are your numbers played back, not measured customer data.
          </p>
        </div>

        {/* Outputs */}
        <SpotlightCard
          aria-live="polite"
          className="rounded-2xl border border-brand/30 bg-brand/5 p-6"
        >
          <p className="text-sm text-muted-foreground">Locked up in late fees each month</p>
          <p className="mt-1 text-4xl font-bold tabular-nums tracking-tight text-brand-deep">
            <CountUp value={lockedUp} format={(n) => formatINR(Math.round(n))} />
          </p>

          <dl className="mt-6 space-y-3 border-t border-brand/20 pt-5 text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">Late payments to chase</dt>
              <dd className="font-semibold tabular-nums text-foreground">
                ~<CountUp value={lateCount} /> a month
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">
                Time on follow-ups (~{MINUTES_PER_FOLLOW_UP} min each)
              </dt>
              <dd className="font-semibold tabular-nums text-foreground">
                ~<CountUp value={hoursChasing} /> hours
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">Scale, per student</dt>
              <dd className="font-semibold tabular-nums text-foreground">
                <CountUp value={perStudent} format={(n) => formatINR(Math.ceil(n))} />
                /month
              </dd>
            </div>
          </dl>

          <p className="mt-5 text-sm leading-relaxed text-foreground">
            {paysForItself
              ? `If automated reminders freed up even 5% of that, Scale (${formatINR(SCALE_PRICE)}/month) pays for itself.`
              : `Scale is ${formatINR(SCALE_PRICE)}/month flat — every reminder, invoice and payment link included.`}
          </p>

          <div className="mt-6">
            <TrialCTA placement="calculator" label="Stop the chase — start free" size="default" />
          </div>
        </SpotlightCard>
      </div>
    </Section>
  );
}
